import React, { useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import { getClients, createContract, getContracts, getContract, updateContract, deleteContract, generateContract, getContractPdfUrl } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import PageHelpButton from '../components/help/PageHelpButton';
import {
  Plus, FileSignature, Trash2, Edit2, Download, Send, Loader2, Bot, ArrowLeft, Eye
} from 'lucide-react';

const STATUS_LABELS = {
  draft: 'Borrador', sent: 'Enviado', signed: 'Firmado', cancelled: 'Cancelado'
};
const STATUS_COLORS = {
  draft: 'bg-stone-100 text-stone-700', sent: 'bg-blue-100 text-blue-700',
  signed: 'bg-green-100 text-green-700', cancelled: 'bg-red-100 text-red-700'
};

const ContractsPage = () => {
  const [contracts, setContracts] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list, create, detail, edit
  const [selectedContract, setSelectedContract] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [modifyPrompt, setModifyPrompt] = useState('');
  const [modifying, setModifying] = useState(false);

  // Create form
  const [selectedClient, setSelectedClient] = useState('');
  const [prompt, setPrompt] = useState('');
  const [generatedContent, setGeneratedContent] = useState('');
  const [contractTitle, setContractTitle] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [contractsRes, clientsRes] = await Promise.all([getContracts(), getClients()]);
      setContracts(contractsRes.data);
      setClients(clientsRes.data);
    } catch (err) {
      toast.error('Error cargando datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleGenerate = async () => {
    if (!selectedClient || !prompt) {
      toast.error('Selecciona un cliente y describe el contrato');
      return;
    }
    setGenerating(true);
    try {
      const { data } = await generateContract({
        client_id: selectedClient,
        prompt: prompt,
        existing_content: null
      });
      setGeneratedContent(data.content);
      setContractTitle(data.title);
      toast.success('Contrato generado por IA');
    } catch (err) {
      toast.error('Error generando contrato');
    } finally {
      setGenerating(false);
    }
  };

  const handleModify = async () => {
    if (!modifyPrompt) return;
    setModifying(true);
    try {
      const clientId = selectedContract?.client_id || selectedClient;
      const { data } = await generateContract({
        client_id: clientId,
        prompt: modifyPrompt,
        existing_content: selectedContract ? selectedContract.content : generatedContent
      });
      if (selectedContract) {
        setSelectedContract(prev => ({ ...prev, content: data.content }));
        await updateContract(selectedContract.id, { content: data.content });
        toast.success('Contrato modificado');
      } else {
        setGeneratedContent(data.content);
        toast.success('Contrato modificado');
      }
      setModifyPrompt('');
    } catch (err) {
      toast.error('Error modificando contrato');
    } finally {
      setModifying(false);
    }
  };

  const handleSave = async () => {
    if (!generatedContent || !selectedClient) return;
    try {
      await createContract({
        client_id: selectedClient,
        title: contractTitle || 'Contrato',
        content: generatedContent,
        status: 'draft'
      });
      toast.success('Contrato guardado');
      setView('list');
      setGeneratedContent('');
      setPrompt('');
      setSelectedClient('');
      fetchData();
    } catch (err) {
      toast.error('Error guardando contrato');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este contrato?')) return;
    try {
      await deleteContract(id);
      toast.success('Contrato eliminado');
      if (view === 'detail') setView('list');
      fetchData();
    } catch (err) {
      toast.error('Error eliminando contrato');
    }
  };

  const handleStatusChange = async (id, status) => {
    try {
      const { data } = await updateContract(id, { status });
      setSelectedContract(data);
      toast.success('Estado actualizado');
      fetchData();
    } catch (err) {
      toast.error('Error actualizando estado');
    }
  };

  const openDetail = async (id) => {
    try {
      const { data } = await getContract(id);
      setSelectedContract(data);
      setView('detail');
    } catch (err) {
      toast.error('Error cargando contrato');
    }
  };

  const renderMarkdown = (text) => {
    if (!text) return '';
    return text
      .replace(/^### (.+)$/gm, '<h3 class="font-bold text-base mt-4 mb-2">$1</h3>')
      .replace(/^## (.+)$/gm, '<h2 class="font-bold text-lg mt-6 mb-3 text-[#0EA5E9]">$1</h2>')
      .replace(/^# (.+)$/gm, '<h1 class="font-bold text-xl mt-4 mb-4 text-center">$1</h1>')
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\n\n/g, '</p><p class="mb-2">')
      .replace(/\n/g, '<br/>');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#FF6B4A]" /></div>;
  }

  // CREATE VIEW
  if (view === 'create') {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <button onClick={() => { setView('list'); setGeneratedContent(''); setPrompt(''); }} className="p-2 hover:bg-stone-100 rounded-xl">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight" data-testid="create-contract-title">Nuevo Contrato</h1>
            <p className="text-stone-500 text-sm">La IA generará el contrato por ti</p>
          </div>
        </div>

        {!generatedContent ? (
          <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6 space-y-4">
            <div>
              <Label>Cliente *</Label>
              <Select value={selectedClient} onValueChange={setSelectedClient}>
                <SelectTrigger className="rounded-xl" data-testid="contract-client-select">
                  <SelectValue placeholder="Selecciona un cliente" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Describe el contrato que necesitas *</Label>
              <Textarea
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                placeholder="Ej: Contrato de servicios de diseño web por 3 meses, 2000€/mes, con cláusula de confidencialidad..."
                className="rounded-xl min-h-[120px]"
                data-testid="contract-prompt-input"
              />
            </div>
            <Button onClick={handleGenerate} disabled={generating || !selectedClient || !prompt} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300 w-full sm:w-auto" data-testid="generate-contract-button">
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bot className="w-4 h-4 mr-2" />}
              {generating ? 'Generando contrato...' : 'Generar con IA'}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6">
              <Input value={contractTitle} onChange={(e) => setContractTitle(e.target.value)} className="rounded-xl text-lg font-bold mb-4 border-0 border-b" placeholder="Título del contrato" data-testid="contract-title-input" />
              <div className="prose prose-sm max-w-none border border-stone-100 rounded-xl p-4 sm:p-6 max-h-[50vh] overflow-y-auto bg-stone-50" data-testid="contract-preview" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`<p>${renderMarkdown(generatedContent)}</p>`) }} />
            </div>

            {/* AI Modify */}
            <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6">
              <Label className="mb-2 block font-medium">Modificar con IA</Label>
              <div className="flex gap-2">
                <Input value={modifyPrompt} onChange={(e) => setModifyPrompt(e.target.value)} placeholder="Ej: Añade cláusula de penalización, cambia duración a 6 meses..." className="rounded-xl flex-1" data-testid="modify-prompt-input" onKeyDown={(e) => e.key === 'Enter' && handleModify()} />
                <Button onClick={handleModify} disabled={modifying || !modifyPrompt} className="bg-[#0EA5E9] hover:bg-[#FF6B4A] rounded-xl" data-testid="modify-contract-button">
                  {modifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <Button variant="outline" onClick={() => setGeneratedContent('')} className="rounded-xl">Regenerar</Button>
              <Button onClick={handleSave} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300" data-testid="save-contract-button">Guardar Contrato</Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // DETAIL VIEW
  if (view === 'detail' && selectedContract) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button onClick={() => { setView('list'); setSelectedContract(null); }} className="p-2 hover:bg-stone-100 rounded-xl">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight">{selectedContract.title}</h1>
                <span className={`text-xs px-2 py-0.5 rounded-xl font-medium ${STATUS_COLORS[selectedContract.status]}`}>
                  {STATUS_LABELS[selectedContract.status]}
                </span>
              </div>
              <p className="text-stone-500 text-sm">{selectedContract.client_name}</p>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <a href={getContractPdfUrl(selectedContract.id)} target="_blank" rel="noreferrer">
              <Button variant="outline" className="rounded-xl" data-testid="download-contract-pdf">
                <Download className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">PDF</span>
              </Button>
            </a>
            {selectedContract.status === 'draft' && (
              <Button onClick={() => handleStatusChange(selectedContract.id, 'sent')} className="bg-blue-600 hover:bg-blue-700 rounded-xl">
                <Send className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Enviar</span>
              </Button>
            )}
            {selectedContract.status === 'sent' && (
              <Button onClick={() => handleStatusChange(selectedContract.id, 'signed')} className="bg-green-600 hover:bg-green-700 rounded-xl">
                <FileSignature className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Firmado</span>
              </Button>
            )}
            <Button variant="outline" onClick={() => handleDelete(selectedContract.id)} className="rounded-xl text-red-600 hover:text-red-700 hover:bg-red-50">
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-8">
          <div className="prose prose-sm max-w-none" data-testid="contract-content" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(`<p>${renderMarkdown(selectedContract.content)}</p>`) }} />
        </div>

        {/* AI Modify */}
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6">
          <Label className="mb-2 block font-medium">Modificar con IA</Label>
          <div className="flex gap-2">
            <Input value={modifyPrompt} onChange={(e) => setModifyPrompt(e.target.value)} placeholder="Ej: Cambia la duración a 1 año, añade cláusula de rescisión..." className="rounded-xl flex-1" data-testid="detail-modify-input" onKeyDown={(e) => e.key === 'Enter' && handleModify()} />
            <Button onClick={handleModify} disabled={modifying || !modifyPrompt} className="bg-[#0EA5E9] hover:bg-[#FF6B4A] rounded-xl">
              {modifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // LIST VIEW
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight" data-testid="contracts-title">Contratos</h1>
            <p className="text-stone-500 text-sm mt-1">Genera y gestiona contratos con IA</p>
          </div>
          <PageHelpButton moduleKey="contracts" variant="icon" />
        </div>
        <Button onClick={() => setView('create')} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300 w-full sm:w-auto" data-testid="new-contract-button">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Contrato
        </Button>
      </div>

      {contracts.length === 0 ? (
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-12 text-center" data-testid="no-contracts">
          <FileSignature className="w-12 h-12 text-stone-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-stone-600 mb-2">Sin contratos</h3>
          <p className="text-stone-400 text-sm mb-4">Pide a la IA que genere un contrato para tus clientes</p>
          <Button onClick={() => setView('create')} className="bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full shadow-lg shadow-orange-200/50 hover:-translate-y-0.5 transition-all duration-300">
            <Plus className="w-4 h-4 mr-2" /> Crear Contrato
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {contracts.map((contract) => (
            <div key={contract.id} className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 hover:border-stone-300 transition-colors cursor-pointer" onClick={() => openDetail(contract.id)} data-testid={`contract-item-${contract.id}`}>
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                  <FileSignature className="w-5 h-5 text-[#FF6B4A]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-medium text-sm truncate">{contract.title}</p>
                    <span className={`text-[10px] sm:text-xs px-2 py-0.5 rounded-xl font-medium ${STATUS_COLORS[contract.status]}`}>
                      {STATUS_LABELS[contract.status]}
                    </span>
                  </div>
                  <p className="text-xs text-stone-500 mt-0.5">{contract.client_name} · {contract.created_at?.split('T')[0]}</p>
                </div>
              </div>
              <div className="flex gap-1 sm:gap-2" onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="sm" onClick={() => openDetail(contract.id)} className="rounded-xl">
                  <Eye className="w-3.5 h-3.5" />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => handleDelete(contract.id)} className="rounded-xl text-red-500 hover:text-red-700">
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ContractsPage;
